import * as React from "react";
import { Alert, Input, Modal, notification, Select } from "antd";
import { linksOf, request } from "../../../services/cpln";
import { RunReplicaTerminationReducerActionType, useRunReplicaTerminationReducer } from "./reducer_replicaTermination";
import { useDeploymentsReducer } from "../../../components/detail/deploymentsReducer";
import { Workload } from "../../../schema/types/workload/entity";
import { useAppSelector } from "../../../store/hooks";
import { selectContext } from "../../../store/contextSlice";
import { fetchGvcLocations } from "../../../mst/kinds/gvc.helpers";
import { parseLink } from "../../../utils/linkParser/linkParser";

interface Props {
  workload: Workload;
  onClose: any;
  onDone: any;
  visible: boolean;
  initialLocation?: string;
  initialReplica?: string;
}

export const CommandModal_ReplicaTermination: React.FC<Props> = ({
  workload,
  onClose,
  onDone,
  visible,
  initialLocation,
  initialReplica,
}) => {
  const { org } = useAppSelector(selectContext);

  const selfLink = linksOf(workload).self!;
  const gvcName = parseLink(selfLink, { useInputCtx: true }).gvc;

  const { state: deploymentsState, fetchDeployments } = useDeploymentsReducer();
  const deployments = deploymentsState.deploymentsMap[selfLink] || [];

  const [gvcLocations, setGvcLocations] = React.useState<string[]>([]);
  const [locationFetchFailed, setLocationFetchFailed] = React.useState(false);

  React.useEffect(() => {
    const unsubscribe = fetchDeployments([selfLink]);
    loadGvcLocations();
    return () => {
      unsubscribe();
    };
  }, []);

  async function loadGvcLocations() {
    if (!gvcName) return;
    try {
      const result = await fetchGvcLocations(gvcName);
      setGvcLocations(result.enabled);
    } catch (e) {
      setLocationFetchFailed(true);
    }
  }

  const [state, dispatch] = useRunReplicaTerminationReducer(initialLocation, initialReplica);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingReplicas, setIsLoadingReplicas] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const handleChangeLocation = async (_location: string) => {
    let _errorMessage: string = "";
    let _replicas: { label: string; value: string }[] = [];

    dispatch({ type: RunReplicaTerminationReducerActionType.LOCATION, payload: _location });

    setErrorMessage("");
    setIsLoadingReplicas(true);

    try {
      const deployment = deployments.find((deployment) => deployment.name === _location);
      if (!deployment || !deployment.status?.remote) {
        _errorMessage = `No${workload.spec.type === "cron" ? " active" : ""} replica was found in this location.`;
      } else {
        const url = `${deployment?.status.remote}/replicas/org/${org}/gvc/${gvcName}/workload/${workload.name}`;
        const res = await request({ service: "self", url: url });
        if (workload.spec.type === "cron") {
          for (let deployment of deployments) {
            if (deployment.name !== _location) continue;
            for (let jobExecution of deployment.status?.jobExecutions || []) {
              for (let replica of res.data.items) {
                if (replica.startsWith(jobExecution.name)) {
                  _replicas.push({ label: replica, value: replica });
                }
              }
            }
          }
        } else {
          _replicas = res.data.items.map((replica: string) => ({ label: replica, value: replica }));
        }
      }
    } catch (e) {
      notification.warning({ message: "Failed to fetch replica list." });
    }

    setIsLoadingReplicas(false);

    if (_replicas.length > 0) {
      console.log("_replicas", _replicas);
      const sortedReplicas = _replicas.slice().sort((a, b) => {
        if (a.label > b.label) return 1;
        if (a.label < b.label) return -1;
        return 0;
      });
      dispatch({ type: RunReplicaTerminationReducerActionType.REPLICA, payload: sortedReplicas[0]?.value || "" });
      dispatch({ type: RunReplicaTerminationReducerActionType.REPLICAS, payload: sortedReplicas });
    } else {
      _errorMessage = `No${workload.spec.type === "cron" ? " active" : ""} replica was found in this location.`;
      dispatch({ type: RunReplicaTerminationReducerActionType.REPLICA, payload: "" });
      dispatch({ type: RunReplicaTerminationReducerActionType.REPLICAS, payload: [] });
    }

    if (_errorMessage) {
      setErrorMessage(_errorMessage);
    }
  };

  async function handleConfirm() {
    setIsLoading(true);
    try {
      await request({
        method: "post",
        url: selfLink + "/-command",
        body: {
          type: "stopReplica",
          spec: { replica: state.replica, location: state.location },
        },
      });
      onDone();
      notification.success({
        message: "Success",
        description: `${workload.spec.type === "stateful" ? "Restarted" : "Stopped"} ${state.replica}`,
      });
    } catch (e) {
      notification.warning({
        message: `Failed to ${workload.spec.type === "stateful" ? "restart" : "stop"} ${state.replica}.`,
        description: e?.response?.data?.message,
      });
    }
    setIsLoading(false);
  }

  let isValid = true;
  if (!state.location) isValid = false;
  if (!state.replica) isValid = false;

  return (
    <Modal
      open={visible}
      title={`${workload.spec.type === "stateful" ? "Restart" : "Stop"} Replica?`}
      okText={`Confirm`}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: isLoading || isLoadingReplicas || !isValid, loading: isLoading || isLoadingReplicas }}
      onCancel={onClose}
      onOk={handleConfirm}
      maskClosable={false}
      destroyOnHidden
    >
      <div>
        {workload.spec.type !== "cron" || errorMessage ? (
          <Alert
            className="mb-4"
            type={errorMessage ? "error" : "info"}
            message={
              errorMessage
                ? errorMessage
                : workload.spec.type === "stateful"
                ? "A new replica will be started"
                : "A new replica might be started depending on the workload scale options."
            }
          />
        ) : null}
        <div>Location</div>
        {locationFetchFailed && gvcLocations.length === 0 ? (
          <Input
            className="mb-4"
            value={state.location}
            onChange={(e) => handleChangeLocation(e.target.value)}
            placeholder="Enter a location"
            style={{ width: "100%" }}
          />
        ) : (
          <Select
            className="mb-4"
            value={state.location}
            onChange={(value) => handleChangeLocation(value)}
            options={gvcLocations.map((loc) => ({ label: loc, value: loc }))}
            style={{ width: "100%" }}
          />
        )}
        <div>Replica</div>
        <Select
          value={state.replica}
          disabled={state.replica === "loading"}
          onChange={(value) => dispatch({ type: RunReplicaTerminationReducerActionType.REPLICA, payload: value })}
          options={state.replicas}
          style={{ width: "100%" }}
        />
      </div>
    </Modal>
  );
};
